import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import type { KeyboardShortcut, ShortcutCategory } from '../../hooks/useKeyboardShortcuts';
import { CATEGORY_LABELS, formatShortcutKey } from '../../hooks/useKeyboardShortcuts';
import './KeyboardShortcutsDialog.css';

interface KeyboardShortcutsDialogProps {
  open: boolean;
  onClose: () => void;
  shortcuts: KeyboardShortcut[];
}

/** Order in which categories appear in the help dialog */
const CATEGORY_ORDER: ShortcutCategory[] = [
  'general',
  'navigation',
  'selective-touch',
  'data-entry',
];

export default function KeyboardShortcutsDialog({
  open,
  onClose,
  shortcuts,
}: KeyboardShortcutsDialogProps) {
  if (!open) return null;

  // Group shortcuts by category, deduplicating by id
  const seen = new Set<string>();
  const grouped = new Map<ShortcutCategory, KeyboardShortcut[]>();

  for (const s of shortcuts) {
    // Keyless entries are command-palette commands ("Go to Lab Data"), not
    // keys; listing them showed thirty rows with an empty key box.
    if (!s.key) continue;
    if (seen.has(s.id)) continue;
    seen.add(s.id);
    if (!grouped.has(s.category)) grouped.set(s.category, []);
    grouped.get(s.category)!.push(s);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby="kbd-dialog-title"
      slotProps={{
        paper: {
          className: 'kbd-help-dialog',
        },
        backdrop: {
          className: 'kbd-help-overlay-backdrop',
        },
      }}
    >
      <DialogTitle id="kbd-dialog-title" className="kbd-help-header" component="div">
        <span className="kbd-help-title">
          <i className="bi bi-keyboard" aria-hidden="true" />
          Keyboard Shortcuts
        </span>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          size="small"
          sx={{ color: 'var(--text-secondary, #6b7280)' }}
        >
          <i className="bi bi-x-lg" />
        </IconButton>
      </DialogTitle>

      <DialogContent className="kbd-help-body" dividers={false}>
        {CATEGORY_ORDER.map((cat) => {
          const items = grouped.get(cat);
          if (!items || items.length === 0) return null;
          return (
            <div className="kbd-category" key={cat}>
              <div className="kbd-category-title">{CATEGORY_LABELS[cat]}</div>
              {items.map((s) => (
                <ShortcutRow key={s.id} shortcut={s} />
              ))}
            </div>
          );
        })}
      </DialogContent>

      <div className="kbd-help-footer">
        Press <span className="kbd-key" style={{ display: 'inline-flex' }}>Esc</span> to close
      </div>
    </Dialog>
  );
}

function ShortcutRow({ shortcut }: { shortcut: KeyboardShortcut }) {
  const combo = formatShortcutKey(shortcut);
  const parts = combo.split(' + ');

  return (
    <div className="kbd-shortcut-row">
      <span className="kbd-shortcut-label">{shortcut.label}</span>
      <span className="kbd-key-combo">
        {parts.map((part, i) => (
          <span key={i}>
            {i > 0 && <span className="kbd-key-separator">+</span>}
            <span className="kbd-key">{part}</span>
          </span>
        ))}
      </span>
    </div>
  );
}
